/** Scrawl — loose, sketchy multi-pass lines. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point, Shape } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { buildSmoothPath, distance, transformPath } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12c3-4 5 4 8 0s5 4 8 0"/><path d="M4 15c3-3 5 3 8 0s4 3 7 1"/></svg>';

export function createScrawl(): CreateModule {
  let pts: Point[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'scrawl',
      kind: 'create',
      name: 'Scrawl',
      apiVersion: 1,
      description: 'Loose, sketchy lines drawn in several jittered passes.',
      icon: ICON,
      params: [
        { kind: 'slider', key: 'mess', label: 'Mess', min: 0, max: 12, step: 0.5, default: 3 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 1.5) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const mess = cfgNum(ctx.config, 'mess', 3);
      const base = buildSmoothPath(pts);
      const col = ctx.ink;
      const out: Shape[] = [];
      for (let k = 0; k < 3; k++) {
        const path =
          k === 0
            ? base
            : transformPath(base, (p) => ({
                x: p.x + (ctx.rng.next() - 0.5) * 2 * mess,
                y: p.y + (ctx.rng.next() - 0.5) * 2 * mess,
              }));
        const style = defaultStyle();
        style.lineWidth = k === 0 ? 1.2 : 0.8;
        style.color = { ...col, a: k === 0 ? 0.9 : 0.5 };
        out.push(ctx.newShape({ path, style }));
      }
      return out;
    },

    onClear() {
      pts = [];
    },

    onCommit() {
      pts = [];
    },
  };
}
